import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Building2, Infinity as InfinityIcon, Check, Zap, Plus, Ruler } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { billingCheckout } from "@/lib/api";
import { detectBillingCurrency, PLAN_PRICING } from "@/lib/billing";
import { formatApiErrorDetail } from "@/lib/AuthContext";

/**
 * Project-limit / room-cap upsell.
 *
 * Variants (driven by `product` + `mode`):
 *  - product="flipcost"  : Single Project (R280 ZAR), Pro / Unlimited as
 *                          coming-soon waitlist tiles.
 *  - product="spacecost" : SpaceCost Project unlock (R180 ZAR) — primary CTA.
 *  - mode="room_pack"    : "+3 rooms" upsell (R80 ZAR) attached to a specific
 *                          SpaceCost project (`projectId` required).
 */
export default function UpgradeModal({
  open,
  onOpenChange,
  currentPlan = "free",
  projectLimit = 1,
  product = "flipcost",
  mode = "project",
  projectId = null,
  roomCap = 3,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const currency = detectBillingCurrency();
  const pricing = PLAN_PRICING[currency];
  const isSpaceCost = product === "spacecost";
  const isRoomPack = mode === "room_pack";

  const themeAttrs = isSpaceCost ? { "data-theme": "spacecost" } : {};

  const goNotify = () => {
    onOpenChange(false);
    navigate("/pricing#notify");
  };

  const startCheckout = async (plan, extra = {}) => {
    setLoading(plan);
    try {
      const callback_url = `${window.location.origin}/billing/callback`;
      const { authorization_url } = await billingCheckout({ plan, currency, callback_url, ...extra });
      window.location.href = authorization_url;
    } catch (err) {
      const code = err?.response?.status;
      if (code === 503) {
        toast.error("Checkout isn't live yet — try again shortly.");
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start checkout");
      }
      setLoading(null);
    }
  };

  // Title & description vary by variant.
  let heading;
  let body;
  if (isRoomPack) {
    heading = `You've reached the ${roomCap}-room cap on this project`;
    body = "Add a +3 room pack to keep building this SpaceCost project — once-off, no subscription. The pack stays on this project forever.";
  } else if (isSpaceCost) {
    heading = projectLimit === 0
      ? "Buy your first SpaceCost project unlock"
      : `You've used your ${projectLimit} SpaceCost unlock${projectLimit === 1 ? "" : "s"}`;
    body = projectLimit === 0
      ? "Each room budget needs a once-off SpaceCost unlock. R180 buys one project (up to 3 rooms) — buy more whenever you start a new space."
      : "Buy another SpaceCost unlock to start a new room budget — pay-as-you-go, no subscription.";
  } else {
    heading = projectLimit === 0
      ? "Buy your first Single Project unlock"
      : `You've hit your ${projectLimit}-project limit`;
    body = projectLimit === 0
      ? "Each project requires a one-off Single Project unlock. Buy one to create your first deal — buy more whenever you start a new one. Subscriptions (Pro / Unlimited) are coming soon."
      : "Buy another Single Project unlock to add a new deal — pay-as-you-go, no subscription. Subscriptions (Pro / Unlimited) are launching soon — join the notify list and we'll email you the moment they're live.";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="upgrade-modal"
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[640px]"
        {...themeAttrs}
      >
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-3 py-1 w-fit">
            <Sparkles className="w-3 h-3" /> {isRoomPack ? "Room pack" : (isSpaceCost ? "SpaceCost unlock" : "Buy a project unlock")}
          </div>
          <DialogTitle className="fc-heading text-[#1C1C1C] text-xl mt-3">{heading}</DialogTitle>
          <DialogDescription className="text-xs text-[#A8A29E]">{body}</DialogDescription>
        </DialogHeader>

        {/* Primary CTA — varies by variant */}
        {isRoomPack ? (
          <button
            type="button"
            data-testid="upgrade-room-pack"
            disabled={loading === "room_pack"}
            onClick={() => startCheckout("room_pack", { project_id: projectId })}
            className="relative text-left rounded-xl border-2 border-sage-300 bg-white hover:bg-sage-50/40 p-5 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            <div className="absolute -top-2.5 left-5">
              <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-sage-300 text-[#1A1917] font-medium">
                Available now
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700 shrink-0">
                  <Plus className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="fc-label text-sage-700">+3 Rooms</div>
                  <div className="text-[13px] text-[#1C1C1C] mt-0.5">Add 3 more rooms to this project · once-off</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="fc-heading text-2xl text-[#1C1C1C]">{pricing.room_pack?.label || "R80"}</div>
                <div className="text-[11px] text-[#78716C]">{pricing.room_pack?.cadence || "one-off · +3 rooms"}</div>
              </div>
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-[#1C1C1C]">
              {loading === "room_pack" ? "Redirecting…" : "Buy a +3 room pack"} <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        ) : isSpaceCost ? (
          <button
            type="button"
            data-testid="upgrade-spacecost-single"
            disabled={loading === "single_spacecost"}
            onClick={() => startCheckout("single_spacecost")}
            className="relative text-left rounded-xl border-2 border-sage-300 bg-white hover:bg-sage-50/40 p-5 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            <div className="absolute -top-2.5 left-5">
              <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-sage-300 text-[#1A1917] font-medium">
                Available now
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700 shrink-0">
                  <Ruler className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="fc-label text-sage-700">SpaceCost Project</div>
                  <div className="text-[13px] text-[#1C1C1C] mt-0.5">+1 project · up to 3 rooms · pay-as-you-go</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="fc-heading text-2xl text-[#1C1C1C]">{pricing.single_spacecost?.label || "R180"}</div>
                <div className="text-[11px] text-[#78716C]">{pricing.single_spacecost?.cadence || "one-off"}</div>
              </div>
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-[#1C1C1C]">
              {loading === "single_spacecost" ? "Redirecting…" : (projectLimit === 0 ? "Buy your first SpaceCost unlock" : "Buy another SpaceCost unlock")} <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        ) : (
          <button
            type="button"
            data-testid="upgrade-single"
            disabled={loading === "single"}
            onClick={() => startCheckout("single")}
            className="relative text-left rounded-xl border-2 border-sage-300 bg-white hover:bg-sage-50/40 p-5 transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            <div className="absolute -top-2.5 left-5">
              <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-sage-300 text-[#1A1917] font-medium">
                Available now
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700 shrink-0">
                  <Zap className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="fc-label text-sage-700">Single Project</div>
                  <div className="text-[13px] text-[#1C1C1C] mt-0.5">+1 project slot · pay-as-you-go</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="fc-heading text-2xl text-[#1C1C1C]">{pricing.single.label}</div>
                <div className="text-[11px] text-[#78716C]">{pricing.single.cadence}</div>
              </div>
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-[#1C1C1C]">
              {loading === "single" ? "Redirecting…" : (projectLimit === 0 ? "Buy your first unlock" : "Buy another unlock")} <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        )}

        {/* Coming-soon subscription tiles — only for FlipCost project upsell. */}
        {!isSpaceCost && !isRoomPack && (
          <>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E] mt-5 mb-1">Or wait for the launch</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="upgrade-pro"
                disabled={loading}
                onClick={goNotify}
                className="relative text-left rounded-xl border border-white/10 bg-[#1C1C1C] text-white hover:border-white/30 p-5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                  <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-sage-300 text-[#1A1917] font-medium inline-flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> Coming soon
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 text-sage-300">
                  <div className="w-8 h-8 rounded-lg bg-sage-300/15 border border-sage-300/30 grid place-items-center">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div className="fc-label text-sage-300">Pro</div>
                </div>
                <ul className="mt-3 space-y-1.5 text-[12px] text-white/85">
                  <li className="flex gap-1.5"><Check className="w-3 h-3 text-sage-300 mt-[3px] shrink-0" /> Up to 5 active projects</li>
                  <li className="flex gap-1.5"><Check className="w-3 h-3 text-sage-300 mt-[3px] shrink-0" /> Share with collaborators</li>
                </ul>
                <div className="mt-4 text-[12px] text-sage-300 inline-flex items-center gap-1">
                  Notify me <ArrowRight className="w-3 h-3" />
                </div>
              </button>

              <button
                type="button"
                data-testid="upgrade-unlimited"
                disabled={loading}
                onClick={goNotify}
                className="relative text-left rounded-xl border border-white/10 bg-[#1C1C1C] text-white hover:border-white/30 p-5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                  <span className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-sage-300 text-[#1A1917] font-medium inline-flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> Coming soon
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 text-sage-300">
                  <div className="w-8 h-8 rounded-lg bg-sage-300/15 border border-sage-300/30 grid place-items-center">
                    <InfinityIcon className="w-4 h-4" />
                  </div>
                  <div className="fc-label text-sage-300">Unlimited</div>
                </div>
                <ul className="mt-3 space-y-1.5 text-[12px] text-white/85">
                  <li className="flex gap-1.5"><Check className="w-3 h-3 text-sage-300 mt-[3px] shrink-0" /> Unlimited active projects</li>
                  <li className="flex gap-1.5"><Check className="w-3 h-3 text-sage-300 mt-[3px] shrink-0" /> Branded PDF reports</li>
                </ul>
                <div className="mt-4 text-[12px] text-sage-300 inline-flex items-center gap-1">
                  Notify me <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            </div>
          </>
        )}

        <div className="text-[11px] text-[#A8A29E] mt-4 text-center">
          Secure checkout via Paystack · South African Rand
        </div>

        <div className="flex justify-end mt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#78716C] hover:text-[#1C1C1C]"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
